<template>
  <v-app id="inspire">
    <SplashScreen v-if="!authStore.user" />

    <!-- App Content (visible when authenticated) -->
    <template v-else>
      <!-- App Bar for Mobile -->
      <v-app-bar v-if="isMobile" app :elevation="2" color="secondary-400" density="comfortable">
        <template #prepend>
          <div class="align-center d-flex left-0 pl-4 position-absolute">
            <icon name="ri:menu-line" size="24" class="menu-icon" @click.stop="drawer = !drawer" />
          </div>
        </template>

        <v-app-bar-title class="align-center d-flex justify-center">
          <NuxtImg src="/icons/leadbook-white.svg" width="124" height="48" />
        </v-app-bar-title>
      </v-app-bar>

      <!-- Sidebar Drawer -->
      <SidebarDrawer
        :key="authStore.user?.id"
        v-model="drawer"
        :selected="selected"
        :items="filteredSidebarItems"
        :actions="filteredSidebarActions"
        :permanent="display.mdAndUp.value"
        :is-mobile="isMobile"
        @update:selected="selected = $event"
        @click:action="handleSidebarAction"
      />

      <!-- Main Content -->
      <v-main>
        <slot />
      </v-main>
    </template>
  </v-app>
</template>

<script setup lang="ts">
import { useTimeoutFn } from '@vueuse/core';
import { useDisplay } from 'vuetify';

import { userRole, type UserRole } from '@/lib/api/schemas';

import { sidebarItems, sidebarActions } from '@/types/sidebar';

const authStore = useAuthStore();

const display = useDisplay();
const isMobile = computed(() => display.smAndDown.value);
const drawer = ref(!isMobile.value);
const selected = ref('dashboard');

const route = useRoute();

watchEffect(() => {
  const matched = [...sidebarItems, ...sidebarActions].find(item => item.to === route.path);
  if (matched) selected.value = matched.value;
});

const { start: launchOnboarding } = useOnboardingGuide();
const { start: scheduleOnboarding, stop: cancelOnboarding } = useTimeoutFn(() => {
  if (!authStore.isAdmin) {
    launchOnboarding();
  }
}, 1000, { immediate: false });

watch(() => authStore.user,
  (user) => {
    if (!user) return;

    const shouldStartOnboarding
            = user.role === userRole.Admin
              && !user.haveSeenGuide;

    if (shouldStartOnboarding) {
      scheduleOnboarding();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  cancelOnboarding();
});

const handleSidebarAction = (actionValue: string) => {
  switch (actionValue) {
    case 'guide':
      launchOnboarding();
      break;
  }
};

const filteredSidebarItems = computed(() => {
  return sidebarItems.filter((item) => {
    if (!item.roles) return true;
    const userRole = authStore.user?.role;
    return userRole ? item.roles.includes(userRole as UserRole) : false;
  });
});

const filteredSidebarActions = computed(() => {
  return sidebarActions.filter((action) => {
    if (!action.roles) return true;
    const userRole = authStore.user?.role;
    return userRole ? action.roles.includes(userRole as UserRole) : false;
  });
});
</script>
